<script setup>

import {computed, onMounted, reactive, ref} from "vue";
import {useRoute, useRouter} from "vue-router";
import {useI18n} from "vue-i18n";
import LoadingIcon from "@/components/LoadingIcon.vue";
import Paginate from "vuejs-paginate-next"
import {accessControlApi, crawl_sites_Api, articleListsApi, articlesApi, sample_articles_Api, prompt_templates_Api, connectionsApi} from "@/api/forwriting_index";
import {datePipe} from "@/utils/datatimeUtils";
import CheckPointLimit from "@/views/forwriting/fw00_check_point_limit.vue"

const route = useRoute()
const router = useRouter();
const { t, te } = useI18n();
const article_list = reactive({
  id: route.params.article_list_id
})

// ==== modal_new ====

const modal_new = reactive({
  show: false,
  crawl_site: {
    site_name: "",
    news_page_url: "",
    match_threshold: 50,
    generate_type_id: null,
    sample_article_id: null,
    prompt_template_id: null,
    connection_id: null,
    ai_model_id: null,
  },
  open() {
    this.crawl_site.site_name = ""
    this.crawl_site.news_page_url = ""
    this.crawl_site.match_threshold = 50
    this.crawl_site.generate_type_id = null
    this.crawl_site.sample_article_id = null
    this.crawl_site.prompt_template_id = null
    this.crawl_site.connection_id = null
    this.crawl_site.ai_model_id = null
    this.show = true
  }
})
const validate_new_gen_settings = () => {
  const missing = []
  if (!modal_new.crawl_site.generate_type_id) missing.push(t("Crawl_sites.generate_type"))
  if (!modal_new.crawl_site.ai_model_id) missing.push(t("Crawl_sites.ai_model"))
  if (!modal_new.crawl_site.sample_article_id) missing.push(t("Crawl_sites.sample_article"))
  if (!modal_new.crawl_site.prompt_template_id) missing.push(t("Crawl_sites.prompt_template"))
  return missing
}
const modal_new_create = async () => {
  const missing = validate_new_gen_settings()
  if (missing.length > 0) {
    alert(`${t("Crawl_sites.required_settings_alert")}\n・${missing.join("\n・")}`)
    return
  }
  isLoading.show = true;
  try {
    const resp = await crawl_sites_Api.new(article_list.id, modal_new.crawl_site)
    if (resp.status !== 200) {
      throw new Error("ERROR");
    }
    await reload_data()
    modal_new.show = false;
  } catch (error) {
    console.error("Create crawl site failed:", error);
    alert(t("Crawl_sites.create_failed"))
  }
  isLoading.show = false;
}

// ==== modal_update ====

const modal_update = reactive({
  show: false,
  crawl_site: {
    site_name: "",
    news_page_url: "",
    match_threshold: 50,
    is_active: true,
    generate_type_id: null,
    sample_article_id: null,
    prompt_template_id: null,
    connection_id: null,
    ai_model_id: null,
  },
  id: -1
})
const handleEdit = async (par_id) => {
  modal_update.id = par_id
  const resp = await crawl_sites_Api.get_1(par_id)
  modal_update.crawl_site.site_name = resp.data.site_name
  modal_update.crawl_site.news_page_url = resp.data.news_page_url
  modal_update.crawl_site.match_threshold = resp.data.match_threshold || 50
  modal_update.crawl_site.is_active = resp.data.is_active
  modal_update.crawl_site.generate_type_id = resp.data.generate_type_id || null
  modal_update.crawl_site.sample_article_id = resp.data.sample_article_id || null
  modal_update.crawl_site.prompt_template_id = resp.data.prompt_template_id || null
  modal_update.crawl_site.connection_id = resp.data.connection_id || null
  modal_update.crawl_site.ai_model_id = resp.data.ai_model_id || null
  modal_update.show = true
}
const validate_gen_settings = () => {
  const missing = []
  if (!modal_update.crawl_site.generate_type_id) missing.push(t("Crawl_sites.generate_type"))
  if (!modal_update.crawl_site.ai_model_id) missing.push(t("Crawl_sites.ai_model"))
  if (!modal_update.crawl_site.sample_article_id) missing.push(t("Crawl_sites.sample_article"))
  if (!modal_update.crawl_site.prompt_template_id) missing.push(t("Crawl_sites.prompt_template"))
  return missing
}

const handleEdit_confirm = async () => {
  const missing = validate_gen_settings()
  if (missing.length > 0) {
    alert(`${t("Crawl_sites.required_settings_alert")}\n・${missing.join("\n・")}`)
    return
  }
  isLoading.show = true;
  try {
    const resp = await crawl_sites_Api.update(modal_update.id, modal_update.crawl_site)
    if (resp.status !== 200) {
      throw new Error("ERROR");
    }
    await reload_data()
    modal_update.show = false
  } catch (error) {
    console.error("Update crawl site failed:", error);
    alert(t("Crawl_sites.update_failed"))
  }
  isLoading.show = false;
}

// ==== modal_del_1 ====

const modal_del_1 = reactive({
  show: false,
  msg: "",
  id: -1,
})
const handleDelete = async (par_id, site_name) => {
  modal_del_1.msg = t("Crawl_sites.delete_confirm", { site_name })
  modal_del_1.id = par_id
  modal_del_1.show = true;
};
const handleDelete_confirm = async () => {
  isLoading.show = true;
  try {
    const resp = await crawl_sites_Api.delete(modal_del_1.id);
    if (resp.status !== 200) {
      throw new Error("ERROR");
    }
    data.items = data.items.filter((item) => item.id !== modal_del_1.id);
    modal_del_1.show = false;
  } catch (error) {
    console.error("Delete crawl site failed:", error);
    alert(t("Crawl_sites.delete_failed"))
  }
  isLoading.show = false;
};

// ==== manual crawl ====

const handleCrawl = async (par_id) => {
  isLoading.show = true;
  try {
    const resp = await crawl_sites_Api.crawl(par_id);
    if (resp.status === 200) {
      alert(t("Crawl_sites.crawl_started"))
    }
  } catch (error) {
    console.error("Crawl execution failed:", error);
    alert(t("Crawl_sites.crawl_failed"))
  }
  isLoading.show = false;
}

// ==== filter ====

const filter = reactive({
  temp: "",
  searchKey: null,
});
const handleFilter = () => {
  filter.searchKey = filter.temp
  page.current = 1
};
const filteredData = computed(() => {
  let temp = data.items || [];
  if (filter.searchKey) {
    const str_filter = filter.searchKey.toLowerCase()
    temp = temp.filter((item) =>
        String(item.site_name).toLowerCase().indexOf(str_filter) > -1
    )
  }
  return temp;
});

// ==== paging ====

const page = reactive({
  current: 1,
  num: 10,
})
const page_count = computed(() => {
  let cc = Math.floor(filteredData.value.length / page.num)
  if (filteredData.value.length % page.num === 0)
    return cc
  return cc + 1
})

// ==== init data... ====

const data = reactive({
  items: [],
})
const reload_data = async () => {
  const resp = await crawl_sites_Api.getAll_filter(article_list.id)
  data.items = resp.data;

  for (let i in data.items) {
    const id = data.items[i].id
    checked.items[id] = false
  }
}
onMounted(async () => {
  if (!await accessControlApi.check_option_access()) {
    await router.push(`/forwriting/options_error`)
    return
  }
  await reload_data()
  point_limit.is_over = await ref_CheckPointLimit.value.check()

  // 自動追加設定を読み込み
  try {
    const articleListResp = await articleListsApi.get_1(article_list.id)
    autoAddSettings.auto_add_enabled = articleListResp.data.auto_add_enabled ?? false
    autoAddSettings.auto_generate_enabled = articleListResp.data.auto_generate_enabled ?? false
  } catch (e) {
    console.error("Failed to load auto_add_settings:", e)
  }

  await load_gen_options()
});

// ==== checked/un-checked ====

const checked = reactive({
  items: {}
})
const onSelectAllChange = async (e) => {
  const checked_val = e.target.checked
  for (const i in checked.items)
    checked.items[i] = checked_val
}

// ==== button refresh ====

const refreshPage = async () => {
  await reload_data()
}

// ==== isLoading ====

const isLoading = reactive({
  show: false
})

// ==== modal_over_points ====
const ref_CheckPointLimit = ref(null)
const point_limit = reactive({
  is_over: false,
})

// ==== 記事生成設定のオプションリスト ====

const gen_options = reactive({
  generate_types: [],
  ai_models: [],
  sample_articles: [],
  prompt_templates: [],
  prompt_templates_content: {},
  connections: [],
})

// 生成タイプは「元ネタから生成」系のみ（id:3, id:4）に絞り込む
const filteredGenerateTypes = computed(() =>
  gen_options.generate_types.filter(gt => gt.id === 3 || gt.id === 4)
)

const getGenerateTypeLabel = (generateType) => {
  const key = `Articles.generate_type_${generateType.id}`
  return te(key) ? t(key) : generateType.generate_type_name
}

const load_gen_options = async () => {
  try {
    const [gtResp, amResp, saResp, ptResp, cnResp] = await Promise.all([
      articlesApi.get_generate_types(),
      articlesApi.get_ai_models(),
      sample_articles_Api.getAll_filter(article_list.id),
      prompt_templates_Api.getAll_filter(article_list.id),
      connectionsApi.getAll_filter(article_list.id),
    ])
    gen_options.generate_types = gtResp.data || []
    gen_options.ai_models = amResp.data || []
    gen_options.sample_articles = saResp.data || []
    gen_options.prompt_templates = ptResp.data || []
    const ptcMap = {}
    for (const item of gen_options.prompt_templates) {
      ptcMap[item.id] = item.prompt_template_content || ""
    }
    gen_options.prompt_templates_content = ptcMap
    gen_options.connections = cnResp.data || []
  } catch (e) {
    console.error("load_gen_options error:", e)
  }
}

// --- language detection from prompt template ---
const _langEntries = [
  { label: "繁體中文", keywords: ["繁體中文", "繁体中文", "Traditional Chinese", "台灣中文", "台灣繁體", "中文", "華語", "華文", "中国語", "tiếng Trung"] },
  { label: "簡體中文", keywords: ["簡體中文", "简体中文", "Simplified Chinese"] },
  { label: "日本語",   keywords: ["日本語", "Japanese", "日語", "日文", "tiếng Nhật", "일본어", "ภาษาญี่ปุ่น"] },
  { label: "한국어",   keywords: ["한국어", "韓国語", "Korean", "韓語", "韓文", "朝鮮語", "조선어", "tiếng Hàn"] },
  { label: "English",  keywords: ["English", "英語", "英文", "英国語", "tiếng Anh", "영어", "ภาษาอังกฤษ"] },
  { label: "ภาษาไทย", keywords: ["ภาษาไทย", "Thai", "泰語", "泰文", "タイ語", "태국어", "tiếng Thái"] },
  { label: "Tiếng Việt", keywords: ["Tiếng Việt", "Vietnamese", "越南語", "越南文", "ベトナム語", "베트남어"] },
  { label: "Español",  keywords: ["Español", "Spanish", "西班牙語", "西班牙文", "スペイン語", "tiếng Tây Ban Nha"] },
  { label: "Français", keywords: ["Français", "French", "法語", "法文", "フランス語", "tiếng Pháp"] },
  { label: "Deutsch",  keywords: ["Deutsch", "German", "德語", "德文", "ドイツ語", "tiếng Đức"] },
]
function _escRe(s) { return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') }
const _targetPats = [
  kw => new RegExp(`使用\\s*${_escRe(kw)}`),
  kw => new RegExp(`用\\s*${_escRe(kw)}\\s*[來来]`),
  kw => new RegExp(`以\\s*${_escRe(kw)}\\s*[來来撰書写]`),
  kw => new RegExp(`${_escRe(kw)}\\s*[來来]\\s*撰寫`),
  kw => new RegExp(`絕對要使用\\s*${_escRe(kw)}`),
  kw => new RegExp(`(?:改寫|改写|翻譯|翻译|轉換|转换|轉成|转成)\\s*(?:為|为|成)\\s*${_escRe(kw)}`),
  kw => new RegExp(`(?:寫成|写成)\\s*${_escRe(kw)}`),
  kw => new RegExp(`(?:請|请)\\s*(?:改寫|改写|翻譯|翻译)\\s*(?:為|为|成)?\\s*${_escRe(kw)}`),
  kw => new RegExp(`(?:轉為|转为|換成|换成|改成|譯為|译为|譯成|译成|轉譯為|转译为)\\s*${_escRe(kw)}`),
  kw => new RegExp(`用\\s*${_escRe(kw)}\\s*(?:寫|写|書寫|书写|輸出|输出|產出|产出|生成|編寫|编写|整理|摘要)`),
  kw => new RegExp(`以\\s*${_escRe(kw)}\\s*(?:書寫|书写|輸出|输出|產出|产出|生成|編寫|编写)`),
  kw => new RegExp(`(?:改用|換用|换用)\\s*${_escRe(kw)}`),
  kw => new RegExp(`(?:重寫|重写|重新撰寫|重新撰写|重新編寫|重新编写)\\s*(?:為|为|成)?\\s*${_escRe(kw)}`),
  kw => new RegExp(`(?:撰寫|撰写|編寫|编写)\\s*(?:成|為|为)\\s*${_escRe(kw)}`),
  kw => new RegExp(`${_escRe(kw)}\\s*で(?:書|執筆|記述|リライト|生成|要約|まとめ)`),
  kw => new RegExp(`${_escRe(kw)}\\s*を用いて`),
  kw => new RegExp(`${_escRe(kw)}\\s*で(?:の|作成|出力|回答)`),
  kw => new RegExp(`${_escRe(kw)}\\s*に\\s*(?:書き換|書き直|翻訳|変換|転換|リライト|直す|直し)`),
  kw => new RegExp(`(?:rewrite|write|output|respond|answer)\\s+(?:in|into)\\s+${_escRe(kw)}`, 'i'),
  kw => new RegExp(`MUST\\s+(?:be\\s+in|write\\s+in|use)\\s+${_escRe(kw)}`, 'i'),
  kw => new RegExp(`(?:rewrite|write|translate)\\s*(?:成|為|为|into)?\\s*${_escRe(kw)}`, 'i'),
  kw => new RegExp(`ต้องใช้\\s*${_escRe(kw)}`),
  kw => new RegExp(`เขียนเป็น\\s*${_escRe(kw)}`),
  kw => new RegExp(`ใช้\\s*${_escRe(kw)}\\s*(?:ทั้งหมด|เท่านั้น|ในการ|เขียน|แปล)`),
  kw => new RegExp(`แปล\\s*(?:เป็น|เข้า)\\s*${_escRe(kw)}`),
  kw => new RegExp(`(?:เขียนใหม่|เรียบเรียง|สรุป|แก้ไข|เปลี่ยน|แปลง|ร่าง|พิมพ์)\\s*(?:เป็น|ใน|ด้วย)\\s*${_escRe(kw)}`),
  kw => new RegExp(`เขียน\\s*(?:ด้วย|เป็น)\\s*${_escRe(kw)}`),
  kw => new RegExp(`ใช้\\s*${_escRe(kw)}\\s*(?:เขียน|แปล|สรุป|เรียบเรียง|ร่าง)`),
  kw => new RegExp(`${_escRe(kw)}\\s*(?:로|으로)\\s*(?:작성|번역|쓰기|쓰다|출력|변환|바꾸|고치|리라이트|생성|요약|정리|편집|작문)`),
  kw => new RegExp(`${_escRe(kw)}\\s*(?:를|을)\\s*(?:사용|쓰다|쓰기)`),
  kw => new RegExp(`${_escRe(kw)}\\s*(?:로|으로)\\s*다시\\s*(?:쓰다|쓰기|작성)`),
  kw => new RegExp(`用\\s*${_escRe(kw)}\\s*(?:rewrite|write|translate|generate|改寫|改写|撰寫|撰写|翻譯|翻译)`),
  kw => new RegExp(`${_escRe(kw)}\\s*(?:で|に)\\s*(?:rewrite|write|translate|generate)`),
]
function _detectScriptLanguage(text) {
  let thai = 0, hiraKata = 0, hangul = 0, cjk = 0, latin = 0, viet = 0
  for (const ch of text) {
    const c = ch.codePointAt(0)
    if (c >= 0x0E00 && c <= 0x0E7F) thai++
    else if ((c >= 0x3040 && c <= 0x309F) || (c >= 0x30A0 && c <= 0x30FF)) hiraKata++
    else if ((c >= 0xAC00 && c <= 0xD7AF) || (c >= 0x1100 && c <= 0x11FF) || (c >= 0x3130 && c <= 0x318F)) hangul++
    else if (c >= 0x4E00 && c <= 0x9FFF) cjk++
    else if ((c >= 0x00C0 && c <= 0x024F) || (c >= 0x1E00 && c <= 0x1EFF)) viet++
    else if (c >= 0x0041 && c <= 0x007A) latin++
  }
  const total = thai + hiraKata + hangul + cjk + latin + viet
  if (total === 0) return null
  if (thai > 0 && thai >= hiraKata && thai >= hangul) return "ภาษาไทย"
  if (hiraKata > 0) return "日本語"
  if (hangul > 0 && hangul >= cjk) return "한국어"
  if (cjk > 0 && cjk >= latin) return "繁體中文"
  if (viet > latin * 0.05 && latin > 0) return "Tiếng Việt"
  if (latin > 0) return "English"
  return null
}
function _detectLangForPtId(ptId) {
  if (!ptId) return null
  const content = gen_options.prompt_templates_content[ptId]
  if (!content) return null
  for (const entry of _langEntries) {
    for (const kw of entry.keywords) {
      if (_targetPats.some(fn => fn(kw).test(content))) return entry.label
    }
  }
  return _detectScriptLanguage(content)
}
const detectedNewLang = computed(() => _detectLangForPtId(modal_new.crawl_site.prompt_template_id))
const detectedUpdateLang = computed(() => _detectLangForPtId(modal_update.crawl_site.prompt_template_id))

// ==== 自動追加設定 ====

const autoAddSettings = reactive({
  auto_add_enabled: false,
  auto_generate_enabled: false,
  saving: false,
})

const save_auto_add_settings = async () => {
  autoAddSettings.saving = true
  try {
    await articleListsApi.update(article_list.id, {
      auto_add_enabled: autoAddSettings.auto_add_enabled,
      auto_generate_enabled: autoAddSettings.auto_generate_enabled,
    })
  } catch (e) {
    console.error("save_auto_add_settings error:", e)
  } finally {
    autoAddSettings.saving = false
  }
}

</script>

<template>

  <!-- isLoading -->
  <LoadingIcon :is-loading="isLoading.show"></LoadingIcon>

  <!-- modal_over_points -->
  <CheckPointLimit ref="ref_CheckPointLimit"></CheckPointLimit>

  <!-- Modal_new -->
  <div v-if="modal_new.show" class="modal-create">
    <div class="modal-content">

      <div class="row">
        <h4>{{ $t("Crawl_sites.modal_new_title") }}</h4>
      </div>

      <div class="row" style="margin-top: 20px">
        <label>{{ $t("Crawl_sites.site_name_label") }}</label>
        <input type="text" class="form-control" style="margin-top: 5px"
               v-model="modal_new.crawl_site.site_name"
               :placeholder="$t('Crawl_sites.site_name_placeholder')"/>
      </div>

      <div class="row" style="margin-top: 15px">
        <label>{{ $t("Crawl_sites.news_page_url_label") }}</label>
        <input type="text" class="form-control" style="margin-top: 5px"
               v-model="modal_new.crawl_site.news_page_url"
               placeholder="https://xxxxxxx/news"/>
      </div>

      <div class="row" style="margin-top: 15px">
        <label>{{ $t("Crawl_sites.match_threshold_label") }}</label>
        <input type="number" class="form-control" style="margin-top: 5px; width: 120px"
               v-model.number="modal_new.crawl_site.match_threshold"
               min="0" max="100"/>
        <small class="text-muted" style="margin-top: 5px">
          {{ $t("Crawl_sites.match_threshold_hint") }}
        </small>
      </div>

      <!-- 記事生成設定 -->
      <div class="row" style="margin-top: 20px">
        <label style="font-weight: 600">{{ $t("Crawl_sites.generation_settings_title") }}</label>
      </div>

      <div class="row" style="margin-top: 10px">
        <label class="gen-label">{{ $t("Crawl_sites.generate_type") }}<span class="required-badge">{{ $t("Crawl_sites.required") }}</span></label>
        <select class="form-select form-select-sm" style="margin-top: 5px"
                v-model.number="modal_new.crawl_site.generate_type_id">
          <option :value="null">{{ $t("Crawl_sites.select_placeholder") }}</option>
          <option v-for="gt in filteredGenerateTypes" :key="gt.id" :value="gt.id">
            {{ getGenerateTypeLabel(gt) }}
          </option>
        </select>
      </div>

      <div class="row" style="margin-top: 10px">
        <label class="gen-label">{{ $t("Crawl_sites.ai_model") }}<span class="required-badge">{{ $t("Crawl_sites.required") }}</span></label>
        <select class="form-select form-select-sm" style="margin-top: 5px"
                v-model.number="modal_new.crawl_site.ai_model_id">
          <option :value="null">{{ $t("Crawl_sites.select_placeholder") }}</option>
          <option v-for="am in gen_options.ai_models" :key="am.id" :value="am.id">
            {{ am.model_name }}
          </option>
        </select>
      </div>

      <div class="row" style="margin-top: 10px">
        <label class="gen-label">{{ $t("Crawl_sites.sample_article") }}<span class="required-badge">{{ $t("Crawl_sites.required") }}</span></label>
        <select class="form-select form-select-sm" style="margin-top: 5px"
                v-model.number="modal_new.crawl_site.sample_article_id">
          <option :value="null">{{ $t("Crawl_sites.select_placeholder") }}</option>
          <option v-for="sa in gen_options.sample_articles" :key="sa.id" :value="sa.id">
            {{ sa.sample_article_name }}
          </option>
        </select>
      </div>

      <div class="row" style="margin-top: 10px">
        <label class="gen-label">{{ $t("Crawl_sites.prompt_template") }}<span class="required-badge">{{ $t("Crawl_sites.required") }}</span>
          <span v-if="detectedNewLang" class="badge bg-info text-dark" style="font-size: 0.72rem; margin-left: 6px; vertical-align: middle;">{{ detectedNewLang }}</span>
        </label>
        <select class="form-select form-select-sm" style="margin-top: 5px"
                v-model.number="modal_new.crawl_site.prompt_template_id">
          <option :value="null">{{ $t("Crawl_sites.select_placeholder") }}</option>
          <option v-for="pt in gen_options.prompt_templates" :key="pt.id" :value="pt.id">
            {{ pt.prompt_template_name }}
          </option>
        </select>
      </div>

      <div class="row" style="margin-top: 10px">
        <label class="gen-label">{{ $t("Crawl_sites.connection") }}</label>
        <select class="form-select form-select-sm" style="margin-top: 5px"
                v-model.number="modal_new.crawl_site.connection_id">
          <option :value="null">{{ $t("Crawl_sites.not_set") }}</option>
          <option v-for="cn in gen_options.connections" :key="cn.id" :value="cn.id">
            {{ cn.connection_name }}
          </option>
        </select>
      </div>

      <!-- 2 buttons-->
      <div class="row" style="margin-top: 30px">
        <button class="btn btn-primary"
                style="width: 120px; margin-left: 20px"
                @click="modal_new_create">
          {{ $t("Crawl_sites.add") }}</button>

        <button class="btn btn-primary" style="width: 120px; margin-left: 20px"
                @click="modal_new.show=false">
          {{ $t("Crawl_sites.back") }}</button>
      </div>

    </div>
  </div>

  <!-- Modal_update -->
  <div v-if="modal_update.show" class="modal-create">
    <div class="modal-content">

      <div class="row">
        <h4>{{ $t("Crawl_sites.modal_edit_title") }}</h4>
      </div>

      <div class="row" style="margin-top: 20px">
        <label>{{ $t("Crawl_sites.site_name_label") }}</label>
        <input type="text" class="form-control" style="margin-top: 5px"
               v-model="modal_update.crawl_site.site_name"/>
      </div>

      <div class="row" style="margin-top: 15px">
        <label>{{ $t("Crawl_sites.news_page_url_label") }}</label>
        <input type="text" class="form-control" style="margin-top: 5px"
               v-model="modal_update.crawl_site.news_page_url"/>
      </div>

      <div class="row" style="margin-top: 15px">
        <label>{{ $t("Crawl_sites.match_threshold_label") }}</label>
        <input type="number" class="form-control" style="margin-top: 5px; width: 120px"
               v-model.number="modal_update.crawl_site.match_threshold"
               min="0" max="100"/>
        <small class="text-muted" style="margin-top: 5px">
          {{ $t("Crawl_sites.match_threshold_hint") }}
        </small>
      </div>

      <div class="row" style="margin-top: 15px">
        <label>
          <input type="checkbox" v-model="modal_update.crawl_site.is_active"/>
          {{ $t("Crawl_sites.active") }}
        </label>
      </div>

      <!-- 記事生成設定 -->
      <div class="row" style="margin-top: 20px">
        <label style="font-weight: 600">{{ $t("Crawl_sites.generation_settings_title") }}</label>
      </div>

      <div class="row" style="margin-top: 10px">
        <label class="gen-label">{{ $t("Crawl_sites.generate_type") }}<span class="required-badge">{{ $t("Crawl_sites.required") }}</span></label>
        <select class="form-select form-select-sm" style="margin-top: 5px"
                v-model.number="modal_update.crawl_site.generate_type_id">
          <option :value="null">{{ $t("Crawl_sites.select_placeholder") }}</option>
          <option v-for="gt in filteredGenerateTypes" :key="gt.id" :value="gt.id">
            {{ getGenerateTypeLabel(gt) }}
          </option>
        </select>
      </div>

      <div class="row" style="margin-top: 10px">
        <label class="gen-label">{{ $t("Crawl_sites.ai_model") }}<span class="required-badge">{{ $t("Crawl_sites.required") }}</span></label>
        <select class="form-select form-select-sm" style="margin-top: 5px"
                v-model.number="modal_update.crawl_site.ai_model_id">
          <option :value="null">{{ $t("Crawl_sites.select_placeholder") }}</option>
          <option v-for="am in gen_options.ai_models" :key="am.id" :value="am.id">
            {{ am.model_name }}
          </option>
        </select>
      </div>

      <div class="row" style="margin-top: 10px">
        <label class="gen-label">{{ $t("Crawl_sites.sample_article") }}<span class="required-badge">{{ $t("Crawl_sites.required") }}</span></label>
        <select class="form-select form-select-sm" style="margin-top: 5px"
                v-model.number="modal_update.crawl_site.sample_article_id">
          <option :value="null">{{ $t("Crawl_sites.select_placeholder") }}</option>
          <option v-for="sa in gen_options.sample_articles" :key="sa.id" :value="sa.id">
            {{ sa.sample_article_name }}
          </option>
        </select>
      </div>

      <div class="row" style="margin-top: 10px">
        <label class="gen-label">{{ $t("Crawl_sites.prompt_template") }}<span class="required-badge">{{ $t("Crawl_sites.required") }}</span>
          <span v-if="detectedUpdateLang" class="badge bg-info text-dark" style="font-size: 0.72rem; margin-left: 6px; vertical-align: middle;">{{ detectedUpdateLang }}</span>
        </label>
        <select class="form-select form-select-sm" style="margin-top: 5px"
                v-model.number="modal_update.crawl_site.prompt_template_id">
          <option :value="null">{{ $t("Crawl_sites.select_placeholder") }}</option>
          <option v-for="pt in gen_options.prompt_templates" :key="pt.id" :value="pt.id">
            {{ pt.prompt_template_name }}
          </option>
        </select>
      </div>

      <div class="row" style="margin-top: 10px">
        <label class="gen-label">{{ $t("Crawl_sites.connection") }}</label>
        <select class="form-select form-select-sm" style="margin-top: 5px"
                v-model.number="modal_update.crawl_site.connection_id">
          <option :value="null">{{ $t("Crawl_sites.not_set") }}</option>
          <option v-for="cn in gen_options.connections" :key="cn.id" :value="cn.id">
            {{ cn.connection_name }}
          </option>
        </select>
      </div>

      <!-- 2 buttons-->
      <div class="row" style="margin-top: 30px">
        <button class="btn btn-primary"
                style="width: 120px; margin-left: 20px"
                @click="handleEdit_confirm">
          {{ $t("Crawl_sites.update") }}</button>

        <button class="btn btn-primary" style="width: 120px; margin-left: 20px"
                @click="modal_update.show=false">
          {{ $t("Crawl_sites.back") }}</button>
      </div>

    </div>
  </div>

  <!-- modal_del_1 -->
  <div v-if="modal_del_1.show" class="modal-del">
    <div class="modal-content">
      <div class="row" style="margin-top: 20px;text-align: center">
        <p>{{ modal_del_1.msg }}</p>
      </div>
      <div class="row" style="margin-top: 20px;margin-left: 10px; margin-right: 10px">
        <button class="btn btn-primary" @click="handleDelete_confirm">OK</button>
      </div>
      <div class="row" style="margin-top: 20px;margin-left: 10px; margin-right: 10px">
        <button class="btn btn-primary" @click="modal_del_1.show = false">{{ $t("Crawl_sites.cancel") }}</button>
      </div>
    </div>
  </div>

  <div class="container">

    <!-- title-->
    <div class="row">
      <h4>{{ $t("Crawl_sites.title") }}</h4>
    </div>

    <!-- 自動追加設定カード -->
    <div class="auto-add-card">
      <div class="auto-add-title">{{ $t("Crawl_sites.auto_add_settings") }}</div>

      <div class="auto-add-row">
        <label class="toggle-label">
          <input type="checkbox" v-model="autoAddSettings.auto_add_enabled" />
          <span class="toggle-text">{{ $t("Crawl_sites.auto_add_enabled") }}</span>
        </label>
        <span class="badge" :class="autoAddSettings.auto_add_enabled ? 'badge-on' : 'badge-off'">
          {{ autoAddSettings.auto_add_enabled ? "ON" : "OFF" }}
        </span>
      </div>

      <div class="auto-add-row" v-show="autoAddSettings.auto_add_enabled">
        <label class="toggle-label">
          <input type="checkbox" v-model="autoAddSettings.auto_generate_enabled" />
          <span class="toggle-text">{{ $t("Crawl_sites.auto_generate_enabled") }}</span>
        </label>
        <span class="badge" :class="autoAddSettings.auto_generate_enabled ? 'badge-on' : 'badge-off'">
          {{ autoAddSettings.auto_generate_enabled ? "ON" : "OFF" }}
        </span>
      </div>

      <button class="btn btn-primary btn-sm"
              style="margin-top: 10px"
              :disabled="autoAddSettings.saving"
              @click="save_auto_add_settings">
        {{ autoAddSettings.saving ? $t("Crawl_sites.saving") : $t("Crawl_sites.save_settings") }}
      </button>
      <p class="auto-add-desc">{{ $t("Crawl_sites.auto_add_desc") }}</p>
    </div>

    <!-- key for filter -->
    <div class="row">
      <div class="column">
        <label for="search-keyword">{{ $t("Crawl_sites.keyword") }}</label>
        <input type="text" id="search-keyword" v-model="filter.temp"
               style="padding: 0 10px; margin: 10px;"/>
        <button class="btn btn-outline-primary"
                @click="handleFilter"
                style="padding: 0 30px; margin: 10px;">
          {{ $t("Crawl_sites.filter") }}</button>
      </div>
    </div>

    <!-- buttons -->
    <div class="row">
      <div class="column" style="text-align: right">

        <button class="btn btn-primary"
                @click="modal_new.open"
                style="padding: 0 50px; margin: 10px;">
          + {{ $t("Crawl_sites.new_item") }}</button>

        <button class="btn btn-outline-primary" @click="refreshPage"
                style="padding: 0 50px; margin: 10px;">
          {{ $t("Crawl_sites.refresh") }}
        </button>

      </div>
    </div>

    <!-- select/de-select -->
    <div class="row">
      <div class="column">
        <input id="select_all" type="checkbox" @change="onSelectAllChange"/>
        <label style="padding: 0 10px; margin: 1px;" for="select_all">
          {{ $t("Crawl_sites.select_all") }}</label>
      </div>
    </div>

    <div class="row">

      <!-- the main table -->
      <table class="table table-hover">

        <thead>
        <tr>
          <th scope="col">{{ $t("Crawl_sites.target") }}</th>
          <th scope="col">{{ $t("Crawl_sites.site_name") }}</th>
          <th scope="col">{{ $t("Crawl_sites.created_at") }}</th>
          <th scope="col">{{ $t("Crawl_sites.operation") }}</th>
        </tr>
        </thead>

        <tbody>
          <tr v-for="(item, index) in filteredData" :key="item.id"
              v-show="(Math.floor(index / page.num) + 1) === page.current"
              style="vertical-align: center">

            <td><input type="checkbox" v-model="checked.items[item.id]"></td>

            <td>{{ item.site_name }}</td>
            <td>{{ datePipe(item.created_at, format = 'yyyy/MM/dd') }}</td>

            <td>
              <button class="btn btn-primary" style="padding: 0 20px"
                      @click="handleEdit(item.id)">
                {{ $t("Crawl_sites.edit") }}</button>

              <button class="btn btn-outline-primary" style="padding: 0 20px; margin-left: 10px"
                      @click="handleCrawl(item.id)">
                {{ $t("Crawl_sites.crawl") }}</button>

              <button class="btn btn-danger" style="padding: 0 20px; margin-left: 10px"
                      @click="handleDelete(item.id, item.site_name)">
                {{ $t("Crawl_sites.delete") }}</button>
            </td>

          </tr>
        </tbody>
      </table>

    </div>
    <div class="row">
      <paginate style="margin-left: 300px; cursor: pointer;"
                v-model="page.current"
                :page-count="page_count"
                :page-range="3"
                :margin-pages="2"
                :prev-text="'<'"
                :next-text="'>'"
      >
      </paginate>
    </div>
  </div>

</template>

<style scoped>
.auto-add-card {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 16px 20px;
  margin-bottom: 20px;
  max-width: 600px;
}
.auto-add-title {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 12px;
  color: #333;
}
.auto-add-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}
.toggle-label {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  font-size: 13px;
}
.toggle-text { color: #444; }
.badge-on { background: #198754; color: #fff; padding: 2px 8px; border-radius: 10px; font-size: 11px; }
.badge-off { background: #adb5bd; color: #fff; padding: 2px 8px; border-radius: 10px; font-size: 11px; }
.auto-add-desc { font-size: 11px; color: #888; margin-top: 8px; margin-bottom: 0; }

.modal-del {
  height: 800px;
  max-height: 1024px;
  width: 480px;
  display: flex;
  justify-content: center;
  align-items: center;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0);
  z-index: 1000;
  top: 100px;
  left: 100px;
  position: absolute;
  padding: 20px;
}

.modal-create {
  height: auto;
  max-height: 1400px;
  width: 960px;
  display: flex;
  justify-content: center;
  align-items: center;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0);
  z-index: 1000;
  top: 100px;
  left: 100px;
  position: absolute;
  padding: 20px;
}

.modal-content {
  background-color: #fff;
  padding: 20px;
  border-radius: 5px;
  max-width: 1600px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
}

.gen-label {
  font-size: 13px;
  margin-bottom: 0;
}
.required-badge {
  background-color: #dc3545;
  color: #fff;
  font-size: 10px;
  padding: 1px 5px;
  border-radius: 3px;
  margin-left: 6px;
  vertical-align: middle;
}
</style>
